<template>
  <!-- Star Rating Wrapper -->
  <div
    class="fe-flex fe-items-center"
    :class="className"
    @mouseleave="resetHover"
  >
    <!-- Loop through number of stars defined by maxStars -->
    <div
      v-for="(star, index) in maxStars"
      :key="index"
      class="fe-relative fe-cursor-pointer"
      :class="sizeClass"
      @mousemove="!readOnly && handleHover(index)"
      @mouseleave="!readOnly && resetHover"
      @click="!readOnly && handleClick(index)"
    >
      <!-- Render empty (unfilled) star as base layer -->
      <JdsIcon
        name="StarIcon"
        :size="size"
        :smSize="smSize"
        :lgSize="lgSize"
        :color="emptyClass"
        :class="[
          'fe-absolute fe-top-0 fe-left-0 fe-pointer-events-none',
          sizeClass,
          'fe-flex-shrink-0 fe-flex-grow-0',
        ]"
      />

      <!-- Render filled portion of the star overlaid on top, with variable width -->
      <div
        class="fe-flex fe-absolute fe-top-0 fe-left-0 fe-h-full fe-overflow-hidden fe-pointer-events-none"
        :style="{ width: getStarFill(index) + '%' }"
      >
        <JdsIcon
          name="StarIcon"
          :size="size"
          :smSize="smSize"
          :lgSize="lgSize"
          :color="filledClass"
          :class="[sizeClass, 'fe-flex-shrink-0 fe-flex-grow-0']"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import JdsIcon from "@/components/DesignSystem/JdsIcon/JdsIcon.vue"

  export default {
    name: "StarTest",
    components: {
      JdsIcon,
    },
    props: {
      // Initial star rating
      initialRating: {
        type: Number,
        default: 0,
      },
      // If true, disables hover and click interactions
      readOnly: {
        type: Boolean,
        default: false,
      },
      // Enables support for partial (fractional) stars
      partialSupport: {
        type: Boolean,
        default: false,
      },
      // Maximum number of stars to display
      maxStars: {
        type: Number,
        default: 5,
      },
      // CSS class for filled (active) stars
      filledClass: {
        type: String,
        default: "fe-text-marigold-40",
      },
      // CSS class for empty (inactive) stars
      emptyClass: {
        type: String,
        default: "fe-text-primary-grey-40",
      },
      // Optional sizing props for responsive variants
      size: {
        type: String,
        default: "m",
        validator: (value) => ["s", "m", "l", "xl", "xxl"].includes(value),
      },
      smSize: {
        type: String,
        default: "",
        validator: (value) => ["s", "m", "l", "xl", "xxl"].includes(value),
      },
      lgSize: {
        type: String,
        default: "",
        validator: (value) => ["s", "m", "l", "xl", "xxl"].includes(value),
      },
      // Additional custom classes for wrapper
      className: {
        type: String,
        default: "",
      },
    },
    data() {
      return {
        // Current selected rating
        currentValue: this.initialRating,
        // Rating shown during hover interaction
        hoverValue: null,
      }
    },
    watch: {
      initialRating(newVal) {
        this.currentValue = newVal
      },
    },
    computed: {
      // Determines the value to be visually displayed (hovered or selected)
      displayValue() {
        return this.hoverValue !== null ? this.hoverValue : this.currentValue
      },
      sizeClass() {
        const widthClasses = {
          "fe-w-base fe-h-base": this.size === "s",
          "fe-w-m fe-h-m": this.size === "m",
          "fe-w-l fe-h-l": this.size === "l",
          "fe-w-xl fe-h-xl": this.size === "xl",
          "fe-w-xxl fe-h-xxl": this.size === "xxl",
          "sm:fe-w-base sm:fe-h-base": this.smSize === "s",
          "sm:fe-w-m sm:fe-h-m": this.smSize === "m",
          "sm:fe-w-l sm:fe-h-l": this.smSize === "l",
          "sm:fe-w-xl sm:fe-h-xl": this.smSize === "xl",
          "sm:fe-w-xxl sm:fe-h-xxl": this.smSize === "xxl",
          "lg:fe-w-base lg:fe-h-base": this.lgSize === "s",
          "lg:fe-w-m lg:fe-h-m": this.lgSize === "m",
          "lg:fe-w-l lg:fe-h-l": this.lgSize === "l",
          "lg:fe-w-xl lg:fe-h-xl": this.lgSize === "xl",
          "lg:fe-w-xxl lg:fe-h-xxl": this.lgSize === "xxl",
        }
        return widthClasses
      },
    },
    methods: {
      // Calculates how much of the star should be filled based on value
      getStarFill(index) {
        const val = this.displayValue
        if (val >= index + 1) return 100
        if (val > index && this.partialSupport) return (val - index) * 100
        return 0
      },
      // On hover, set the temporary hover value
      handleHover(index) {
        this.hoverValue = index + 1
      },
      // Resets hover state when mouse leaves
      resetHover() {
        this.hoverValue = null
      },
      // Sets the rating on click and emits it to parent
      handleClick(index) {
        this.currentValue =
          this.hoverValue !== null ? this.hoverValue : index + 1

        this.$emit("updateRating", this.currentValue)
      },
    },
  }
</script>
